import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { EVENTS, track } from "@/lib/analytics";
import { orderStateKey } from "@/lib/storageKeys";

/** Every dish in the cart, counting quantities rather than lines. */
const countItems = (cart) => (cart || []).reduce((n, it) => n + (it.qty || 0), 0);

/**
 * A cart change, by dish id rather than by name.
 *
 * The id is stable and the same in every language; the guest-facing name belongs
 * to the restaurant and changes when it renames a dish, which would silently
 * split one dish into two rows in a report that spans the rename.
 */
const trackCartChange = (event, dishId, props) => {
  if (!dishId) return;
  track(event, { dish_id: dishId, ...props });
};

const defaultState = {
  // tableLocked/tableToken are set when the guest arrived through a signed QR.
  //
  // tableToken is the *dine-in session* the server minted for that scan, never
  // the code printed on the card. The card is permanent and this state outlives
  // the meal, so keeping the card here would let a guest order from home the next
  // day — the exact thing scanning is supposed to prevent. The session dies on
  // its own, and `tableSessionExpiresAt` (unix seconds) is when.
  guest: {
    table: "",
    name: "",
    allergy: "",
    noSeasoning: false,
    served: "plate",
    // "dine_in" | "takeaway" — the kitchen plates these differently and the
    // ticket has to say which before anyone starts cooking.
    fulfillment: "dine_in",
    tableLocked: false,
    tableToken: null,
    tableSessionExpiresAt: null,
  },
  // [{ uid, dishId, qty, options: {doneness?, style?, milk?, variant?, note?}, source: "ai"|"manual" }]
  cart: [],
  orderNote: "",
  // request_id of the AI plate these cart lines came from — sent with the order
  // so the owner dashboard can attribute revenue to Auto Plate.
  aiRequestId: null,
  // Set when this cart was filled from an earlier order. The server counts a
  // repeat beside the AI-vs-manual comparison rather than inside either half of
  // it: it is real revenue, but it says nothing about what a fresh guest spends.
  reorderOf: null,
  lastOrder: null,
};

const OrderCtx = createContext(null);

/** Unix seconds, to compare against the expiry the server handed back. */
export const nowSeconds = () => Math.floor(Date.now() / 1000);

/** Is there still a live scan behind this state? */
export const hasLiveTableSession = (guest) =>
  Boolean(guest?.tableToken) && Number(guest?.tableSessionExpiresAt || 0) > nowSeconds();

/**
 * Forget a session that has run out.
 *
 * This is the line that answers "the link is still in my phone": the state comes
 * back from localStorage tomorrow with a token the server would refuse anyway, so
 * the storefront stops pretending the guest is at a table and asks them to scan.
 * The table number is cleared with it — a locked table with no session behind it
 * would be a number nobody verified.
 */
function dropExpiredSession(state) {
  if (!state.guest?.tableToken || hasLiveTableSession(state.guest)) return state;
  return {
    ...state,
    guest: {
      ...state.guest,
      table: "",
      tableLocked: false,
      tableToken: null,
      tableSessionExpiresAt: null,
    },
  };
}

const newUid = (dishId) =>
  `${dishId}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

/**
 * Which AI answer this cart still belongs to.
 *
 * A guest who accepts a plate and then deletes every line from it has not
 * ordered from the AI, and the order used to carry the request id anyway — so
 * the dashboard counted it as converted while the money sat in the manual
 * bucket. Two numbers about one order, disagreeing.
 */
const aiIdFor = (cart, current) => (cart.some((it) => it.source === "ai") ? current : null);

/**
 * OrderProvider — cart + guest details for ONE restaurant. `slug` namespaces the
 * localStorage key so two open tabs at two restaurants never share a cart.
 * Mount it with `key={slug}` so a tenant switch starts from that tenant's state.
 */
export const OrderProvider = ({ slug, children }) => {
  const storageKey = orderStateKey(slug);

  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        // `guest` needs its own merge, not just the top-level spread: a field
        // added to defaultState.guest after a guest already persisted (e.g.
        // `fulfillment`) must hydrate with its default, not `undefined`.
        return dropExpiredSession({
          ...defaultState,
          ...parsed,
          guest: { ...defaultState.guest, ...(parsed.guest || {}) },
        });
      }
    } catch (e) {
      // ignore parse errors
    }
    return defaultState;
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(state));
    } catch (e) {
      // ignore storage errors
    }
  }, [state, storageKey]);

  const api = useMemo(
    () => ({
      state,
      setGuest: (guest) => setState((s) => ({ ...s, guest: { ...s.guest, ...guest } })),
      addToCart: (dishId, options = {}, source = "manual") => {
        // Outside the updater on purpose: React invokes an updater twice in
        // StrictMode, and an event fired in there would count every add twice.
        trackCartChange(EVENTS.CART_ITEM_ADDED, dishId, {
          source,
          via: "screen",
          with_options: Object.keys(options || {}).length > 0,
          cart_items_before: countItems(state.cart),
        });
        setState((s) => {
          // collapse identical items (same dish + options + source) into qty
          const sig = JSON.stringify({ dishId, options, source });
          const idx = s.cart.findIndex(
            (it) =>
              JSON.stringify({
                dishId: it.dishId,
                options: it.options,
                source: it.source || "manual",
              }) === sig
          );
          if (idx !== -1) {
            const next = [...s.cart];
            next[idx] = { ...next[idx], qty: next[idx].qty + 1 };
            return { ...s, cart: next };
          }
          return {
            ...s,
            cart: [...s.cart, { uid: newUid(dishId), dishId, qty: 1, options, source }],
          };
        });
      },
      decrementCart: (uid) => {
        const line = state.cart.find((it) => it.uid === uid);
        trackCartChange(EVENTS.CART_ITEM_REMOVED, line?.dishId, {
          source: line?.source || "manual",
          how: "decrement",
          cart_items_before: countItems(state.cart),
        });
        setState((s) => {
          const next = s.cart
            .map((it) => (it.uid === uid ? { ...it, qty: it.qty - 1 } : it))
            .filter((it) => it.qty > 0);
          return { ...s, cart: next, aiRequestId: aiIdFor(next, s.aiRequestId) };
        });
      },
      removeCart: (uid) => {
        const line = state.cart.find((it) => it.uid === uid);
        trackCartChange(EVENTS.CART_ITEM_REMOVED, line?.dishId, {
          source: line?.source || "manual",
          how: "remove",
          qty: line?.qty,
          cart_items_before: countItems(state.cart),
        });
        setState((s) => {
          const next = s.cart.filter((it) => it.uid !== uid);
          return { ...s, cart: next, aiRequestId: aiIdFor(next, s.aiRequestId) };
        });
      },
      clearCart: () => {
        track(EVENTS.CART_CLEARED, { cart_items_before: countItems(state.cart) });
        setState((s) => ({ ...s, cart: [], orderNote: "", aiRequestId: null, reorderOf: null }));
      },
      setOrderNote: (note) => setState((s) => ({ ...s, orderNote: note })),
      addPlateToCart: (plateItems, aiRequestId = null) => {
        // Every line that enters a cart is one `cart_item_added`, whichever
        // gesture put it there — `via` tells them apart. Otherwise a chart of
        // what guests add would be a chart of what guests add *by hand*, and the
        // AI's own dishes would be missing from it.
        (plateItems || []).forEach((d) =>
          trackCartChange(EVENTS.CART_ITEM_ADDED, d.id, { source: "ai", via: "plate" }),
        );
        setState((s) => {
          const items = plateItems.map((d) => {
            const options = {};
            if (d.note) {
              // AI returns a note like "Medium Rare" or "Poached" — split heuristically.
              const DONENESS = ["Rare", "Medium Rare", "Medium", "Medium Well", "Well Done"];
              const STYLE = ["Sliced", "Smashed", "Fried", "Poached", "Scrambled", "Boiled", "Omelette"];
              if (DONENESS.includes(d.note)) options.doneness = d.note;
              else if (STYLE.includes(d.note)) options.style = d.note;
              else options.note = d.note;
            }
            return {
              uid: newUid(d.id),
              dishId: d.id,
              qty: 1,
              options,
              // Drives the AI-sales metric on the owner dashboard — only lines that
              // really came from an accepted AI plate may be tagged "ai".
              source: "ai",
              aiRequestId: aiRequestId || null,
            };
          });
          // Re-accepting from the same answer is "changed my mind", not "one more" —
          // the server's accept endpoint is last-write-wins for the same reason. A
          // re-roll mints a new request id, so a second diner's plate still appends.
          const kept = s.cart.filter(
            (it) => !(it.source === "ai" && aiRequestId && it.aiRequestId === aiRequestId)
          );
          return {
            ...s,
            cart: [...kept, ...items],
            aiRequestId: aiRequestId || s.aiRequestId,
          };
        });
      },
      /**
       * Refill the cart from an earlier order, keeping who composed each line.
       *
       * Re-adding everything as "manual" booked every repeat of an AI plate as a
       * manual sale — the AI losing the credit precisely for the guests it won
       * over, and the owner's uplift quietly understated by its best cohort.
       */
      addReorderToCart: (order) => {
        (order.items || []).forEach((it) =>
          trackCartChange(EVENTS.CART_ITEM_ADDED, it.dish_id, {
            source: it.source === "ai" ? "ai" : "manual",
            via: "reorder",
          }),
        );
        setState((s) => {
          const items = (order.items || []).map((it) => ({
            uid: newUid(it.dish_id),
            dishId: it.dish_id,
            qty: it.qty || 1,
            options: it.options || {},
            source: it.source === "ai" ? "ai" : "manual",
          }));
          const cart = [...s.cart, ...items];
          // Only one answer can back an order. A plate accepted in this sitting
          // wins over the repeated one: those lines are the ones the server can
          // still check against it.
          const carried = s.cart.some((it) => it.source === "ai") ? s.aiRequestId : null;
          return {
            ...s,
            cart,
            aiRequestId: aiIdFor(cart, carried || order.ai_request_id || null),
            reorderOf: order.order_id || null,
          };
        });
      },
      placeOrder: (orderSummary) =>
        setState((s) => ({
          ...s,
          lastOrder: orderSummary,
          cart: [],
          orderNote: "",
          aiRequestId: null,
          reorderOf: null,
        })),
      // Server-priced result of POST /api/orders — what the guest is actually
      // charged, kept for the confirmation and payment screens.
      setOrderResult: (result) =>
        setState((s) => (s.lastOrder ? { ...s, lastOrder: { ...s.lastOrder, server: result } } : s)),
      resetAll: () => setState(defaultState),
    }),
    [state]
  );

  return <OrderCtx.Provider value={api}>{children}</OrderCtx.Provider>;
};

export const useOrder = () => {
  const ctx = useContext(OrderCtx);
  if (!ctx) throw new Error("useOrder must be inside OrderProvider");
  return ctx;
};
